import Box from '@mui/material/Box';

import { AppImage } from '@/components/atoms';

import { type ViewerImage } from './types';
import { VIEWER_CONTROL_BORDER } from './viewerTokens';

type Props = {
  images: readonly ViewerImage[];
  index: number;
  onSelect: (index: number) => void;
};

/** Wide enough to recognise a car in, small enough that twelve fit on a phone. */
const THUMB_WIDTH = 72;

/**
 * The strip along the foot of the lightbox.
 *
 * It answers "how much more is there, and is the one I want further along" —
 * which a counter reading 3 / 12 does not. It is a list of buttons and not a
 * scroller of images, so Tab reaches every one of them and a screen reader is
 * told which is current.
 *
 * The active thumbnail is scrolled into view by the browser rather than by
 * code: `scrollIntoView` on each render fights the visitor's own scrolling,
 * and the thumbnails are small enough that the rail rarely needs to move. The
 * one place it must is when paging with the arrow keys past the end of the
 * visible strip, which focus handles on its own — the active button is the one
 * with `aria-current`, and the browser keeps focus visible.
 *
 * Hidden entirely below two images: a rail with one thumbnail in it is chrome
 * that explains nothing.
 */
export function LightboxThumbRail({ images, index, onSelect }: Props) {
  if (images.length < 2) return null;

  return (
    <Box
      component="ul"
      aria-label="Photographs in this gallery"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'flex',
        gap: 2,
        justifyContent: { md: 'center' },
        overflowX: 'auto',
        px: { xs: 3, md: 5 },
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 4px)',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {images.map((image, i) => (
        <Box component="li" key={image.id} sx={{ flex: '0 0 auto' }}>
          <Box
            component="button"
            type="button"
            aria-label={`Photograph ${i + 1} of ${images.length}`}
            aria-current={i === index}
            onClick={() => onSelect(i)}
            sx={{
              display: 'block',
              p: 0,
              width: THUMB_WIDTH,
              aspectRatio: '1 / 1',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              bgcolor: 'rgba(255, 255, 255, 0.08)',
              border: '2px solid',
              borderColor: i === index ? 'primary.main' : VIEWER_CONTROL_BORDER,
              // The current thumbnail is full strength and the rest step back,
              // so the strip reads at a glance without relying on the border
              // colour alone.
              opacity: i === index ? 1 : 0.6,
              transition: 'opacity 160ms ease, border-color 160ms ease',
              '&:hover, &:focus-visible': { opacity: 1 },
            }}
          >
            <AppImage
              src={image.src}
              alt=""
              width={THUMB_WIDTH}
              height={THUMB_WIDTH}
              sizes={`${THUMB_WIDTH}px`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
